import { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { buildWhatsappUrl, whatsappConfigured } from "@/context/CartContext";

/**
 * FloatingWhatsApp — Apple-inspired assistant button.
 *
 * Four visual layers (all decorative, pointer-events:none):
 *  1. Ambient halo (very soft diffused glow)
 *  2. Two expanding pearl-white waves (staggered, restful cycle)
 *  3. Main surface with radial highlight + dimensional gradient
 *  4. Icon + occasional light sweep
 *
 * Motion states via `data-state`:
 *   - "entering": initial 0.7s reveal (fade + up + scale-in)
 *   - "idle": normal breathing + wave cycle
 *   - "hover": waves paused, surface lifts, label reveals (desktop)
 *   - "attention": one-shot intensified pulse (called after 14s idle)
 *   - "dismissed": interaction detected → animations reduced permanently
 *
 * All timing driven by CSS keyframes. React only sets `data-state`.
 */
export default function FloatingWhatsApp() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const [state, setState] = useState("hidden");
  const [showLabel, setShowLabel] = useState(false);
  const attentionFired = useRef(false);
  const interacted = useRef(false);

  useEffect(() => {
    // Entrance: waits 1.6s after mount for the page to stabilize.
    const t1 = window.setTimeout(() => setState("entering"), 1600);
    const t2 = window.setTimeout(() => setState("idle"), 1600 + 800);

    // Mobile microcopy: reveal small "Need help choosing?" label once
    // per session, ~5s after entry, for ~4s. Skip on hover-capable
    // desktops (they get the hover label instead).
    let t3, t4;
    const canHover = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;
    const shownThisSession = sessionStorage.getItem("kelo_wa_hint_shown");
    if (!canHover && !shownThisSession) {
      t3 = window.setTimeout(() => {
        setShowLabel(true);
        sessionStorage.setItem("kelo_wa_hint_shown", "1");
      }, 5200);
      t4 = window.setTimeout(() => setShowLabel(false), 5200 + 4200);
    }

    // Contextual attention: after ~14s without user interaction with
    // the button, trigger one slightly more visible invitation cycle.
    const attentionTimer = window.setTimeout(() => {
      if (attentionFired.current || interacted.current) return;
      attentionFired.current = true;
      setState("attention");
      window.setTimeout(() => {
        if (!interacted.current) setState("idle");
      }, 3400);
    }, 14000);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
      window.clearTimeout(attentionTimer);
    };
  }, []);

  const onInteract = () => {
    interacted.current = true;
    setState("dismissed");
  };

  if (!whatsappConfigured()) return null;

  const label =
    t?.whatsapp?.hint || "Need help choosing?";

  return (
    <a
      ref={ref}
      data-testid="floating-whatsapp"
      data-state={state}
      href={buildWhatsappUrl(t.whatsapp.support)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with Kelo Diamonds on WhatsApp"
      onPointerEnter={() => state === "idle" && setState("hover")}
      onPointerLeave={() => state === "hover" && setState("idle")}
      onClick={onInteract}
      onTouchStart={onInteract}
      className="wa-assistant fixed z-40 bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] lg:bottom-[calc(1.75rem+env(safe-area-inset-bottom,0px))]"
      style={{
        right: "max(1rem, env(safe-area-inset-right, 0px))",
      }}
    >
      <span className="wa-halo" aria-hidden="true" />
      <span className="wa-wave wa-wave-1" aria-hidden="true" />
      <span className="wa-wave wa-wave-2" aria-hidden="true" />
      <span className="wa-surface" aria-hidden="true">
        <span className="wa-light-sweep" />
        <MessageCircle className="wa-icon" strokeWidth={1.9} />
      </span>
      <span className="wa-label" data-visible={showLabel ? "1" : "0"} aria-hidden="true">
        {label}
      </span>
    </a>
  );
}
